import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TrendingUp, Clock, Receipt, FileSignature } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await signup({ name: form.name, business_name: form.business_name || form.name, email: form.email, password: form.password });
      }
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Clock, text: "Log hours with a one-tap live timer" },
    { icon: Receipt, text: "Generate print-ready PDF invoices" },
    { icon: FileSignature, text: "Sign standard agreements in seconds" },
    { icon: TrendingUp, text: "Forecast weekly & monthly income" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10" style={{ background: "linear-gradient(135deg, hsl(196 84% 32%), hsl(187 72% 40%))" }}>
        <div className="flex items-center gap-2.5 text-white">
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center text-white font-bold font-display text-lg">O</div>
          <span className="font-semibold font-display text-lg">Freelancer Command Center</span>
        </div>
        <div className="text-white">
          <h2 className="text-4xl font-semibold font-display leading-tight mb-8">Run your freelance<br />business like a pro.</h2>
          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><f.icon className="h-5 w-5" /></div>
                <span className="text-white/90">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/70 text-sm">Clients · Jobs · Agreements · Timesheets · Invoices · Forecast</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md rounded-2xl border bg-card p-6 sm:p-8 shadow-[var(--shadow-md)] animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold">O</div>
            <span className="font-semibold font-display">Command Center</span>
          </div>
          <h1 className="text-2xl font-semibold font-display">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            {mode === "login" ? "Sign in to your command center." : "Start managing your freelance work today."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" data-testid="auth-name-input" required value={form.name} onChange={set("name")} placeholder="Osi Adeyemi" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="business">Business name <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="business" data-testid="auth-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Osi Studio" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" required value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" required value={form.password} onChange={set("password")} placeholder="••••••••" />
            </div>
            <Button type="submit" className="w-full" data-testid="auth-submit-button" disabled={loading}>
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-center text-muted-foreground mt-6">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode"
              className="text-primary font-medium hover:underline"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
